import { reportMisActivity } from '../api/misAudit';
import { useMisPageAudit, useMisFilterAudit } from '../api/useMisPageAudit';
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {
    useNavigate
} from "react-router-dom";
import axios from "../api/axiosInstance";
import Swal from "sweetalert2";

/* =========================
   DATE FORMATTER
========================= */
const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}-${mm}-${yy}`;
};

const formatSelectedDate = (value) => {
    if (!value) return "";
    const [year, month, day] = value.split("-");
    return year && month && day ? `${day}-${month}-${year}` : value;
};

function WagesCalculator() {
  useMisPageAudit('wages-calculation');
    const navigate = useNavigate();

    /* =========================
       MONTH + YEAR
    ========================= */
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const selectedPeriod = `${year}-${String(month).padStart(2, "0")}`;
  useMisFilterAudit('wages-calculation', { month: selectedPeriod });

    /* =========================
       STATE
    ========================= */
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // NEW STATES ADDED FOR BUTTON LOADERS
    const [isExportingPaymentMandate, setIsExportingPaymentMandate] = useState(false);
    const [isExportingPayroll, setIsExportingPayroll] = useState(false);
    const [lastWagesDate, setLastWagesDate] = useState("");
    const [isSavingLastWagesDate, setIsSavingLastWagesDate] = useState(false);
    const lastWagesDatePickerRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [kpi, setKpi] = useState({
        totalAmount: 0,
        completedLotAmount: 0,
        wipAmount: 0,
    });

    const [summaryRows, setSummaryRows] = useState([]);
    const [lotRows, setLotRows] = useState([]);
    const [paymentLogicRows, setPaymentLogicRows] = useState([]);

    /* =========================
       PAGINATION
    ========================= */
    const rowsPerPage = 10;
    const [summaryPage, setSummaryPage] = useState(1);
    const [lotPage, setLotPage] = useState(1);
    const [paymentPage, setPaymentPage] = useState(1);

    /* =========================
       FETCH DATA
    ========================= */
    const fetchData = async () => {
        try {
            setLoading(true);

            const [
                kpiRes,
                summaryRes,
                lotRes,
                paymentRes,
            ] = await Promise.all([
                axios.get(`/wages/summary?month=${selectedPeriod}`),
                axios.get(`/wages/monthly?month=${selectedPeriod}`),
                axios.get(`/wages/monthly-lotwise?month=${selectedPeriod}`),
                axios.get(`/wages/payment-logic?month=${selectedPeriod}`),
            ]);

            setKpi(kpiRes.data || {});
            setSummaryRows(summaryRes.data || []);
            setLotRows(lotRes.data || []);
            setPaymentLogicRows(paymentRes.data || []);

            setSummaryPage(1);
            setLotPage(1);
            setPaymentPage(1);
        } catch (err) {
            console.error("Fetch error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [year, month]);

    /* =========================
       REFRESH (RUN SPs)
    ========================= */
    const refreshWages = async () => {
        try {
            setRefreshing(true);
            await axios.post("/wages/refresh");
            await fetchData();
            alert("Wages data refreshed successfully");
        } catch {
            alert("Failed to refresh wages data");
        } finally {
            setRefreshing(false);
        }
    };

    /* =========================
       EXPORT HELPERS
    ========================= */
    const exportSingle = async (url, filename) => {
        const res = await axios.get(url, {
            responseType: "blob"
        });
        const blob = new Blob([res.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      reportMisActivity('EXPORT_DOWNLOAD_TRIGGERED', 'wages-calculation', { filename: link.download });
        window.URL.revokeObjectURL(link.href);
    };

    const exportSummary = () =>
        exportSingle(
            `/wages/export/monthly?month=${selectedPeriod}`,
            `Wages_Summary_${selectedPeriod}.xlsx`
        );

    const exportLotWise = () =>
        exportSingle(
            `/wages/export/monthly-lotwise?month=${selectedPeriod}`,
            `Wages_LotWise_${selectedPeriod}.xlsx`
        );

    const saveLastWagesDate = async () => {
        if (!lastWagesDate) {
            Swal.fire({
                icon: "warning",
                title: "Date Required",
                text: "Please select a date first.",
            });
            return;
        }

        setIsSavingLastWagesDate(true);
        try {
            await axios.post("/wages/last-wages-date", { date: lastWagesDate });

            Swal.fire({
                icon: "success",
                title: "Saved!",
                text: "Last wages date updated successfully.",
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error("Error saving last wages date:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.error || "Failed to save the date.",
            });
        } finally {
            setIsSavingLastWagesDate(false);
        }
    };

    /* =========================
       UPLOAD CONFIRMED WAGES
    ========================= */
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);

        setIsUploading(true);
        try {
            const response = await axios.post("/wages/upload-confirmed", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            Swal.fire({
                icon: "success",
                title: "Uploaded!",
                text: response.data.message,
            });
            fetchData(); // refresh tables
        } catch (error) {
            console.error("Upload Error:", error);
            Swal.fire({
                icon: "error",
                title: "Upload Failed",
                text: error.response?.data?.error || "Failed to upload confirmed wages",
            });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // UPDATE: ADDED LOADING STATE TO EXPORT PAYMENT MANDATE
    const exportPaymentMandate = async () => {
        try {
            setIsExportingPaymentMandate(true);
            await exportSingle(
                `/wages/export/payment-mandate?month=${selectedPeriod}`,
                `Wages_Payment_Mandate_${selectedPeriod}.xlsx`
            );
        } catch (error) {
            alert("Export Payment + Mandate failed");
        } finally {
            setIsExportingPaymentMandate(false);
        }
    };

    /* =========================
       HELPERS
    ========================= */
    const paginate = (rows, page) =>
        rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const summaryCols = useMemo(
        () => (summaryRows[0] ? Object.keys(summaryRows[0]) : []),
        [summaryRows]
    );
    const lotCols = useMemo(
        () => (lotRows[0] ? Object.keys(lotRows[0]) : []),
        [lotRows]
    );
    const paymentCols = useMemo(
        () => (paymentLogicRows[0] ? Object.keys(paymentLogicRows[0]) : []),
        [paymentLogicRows]
    );

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    useMisFilterAudit("wages-calculation", { table: "Payroll", fromDate, toDate });

    // UPDATE: ADDED LOADING STATE TO EXPORT PAYROLL
    const exportPayroll = async () => {
        if (!fromDate || !toDate) {
            alert("Please select From & To Date");
            return;
        }

        try {
            setIsExportingPayroll(true);
            const response = await axios.get(
                `/payroll/export?fromDate=${fromDate}&toDate=${toDate}`, {
                    responseType: "blob"
                }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Payroll_${fromDate}_to_${toDate}.xlsx`;
            link.click();
      reportMisActivity('EXPORT_DOWNLOAD_TRIGGERED', 'wages-calculation', { filename: link.download });
        } catch {
            alert("Export failed");
        } finally {
            setIsExportingPayroll(false);
        }
    };

    /* =========================
       UI
    ========================= */
    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            {/* TOP BAR */}
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => navigate(-1)} className="px-3 py-1 bg-gray-700 text-white rounded text-sm">
                    ⬅Back
                </button>

                <div className="flex items-center gap-3 bg-white border rounded px-4 py-2 shadow">
                    <button onClick={() => setYear(y => y - 1)}>◀</button>
                    <span className="font-semibold">{year}</span>
                    <button onClick={() => setYear(y => y + 1)}>▶</button>

                    <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="border rounded px-2 py-1 text-sm">
                        {[
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December",
                        ].map((m, i) => (
                            <option key={m} value={i + 1}>{m}</option>
                        ))}
                    </select>

                    <button onClick={refreshWages} disabled={refreshing} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                        {refreshing ? "Refreshing…" : "Refresh"}
                    </button>

                    <button onClick={exportPaymentMandate} disabled={isExportingPaymentMandate} className={`px-3 py-1 text-white rounded text-sm flex items-center justify-center ${isExportingPaymentMandate ? "bg-green-500 cursor-not-allowed opacity-75" : "bg-green-700"}`}>
                        {isExportingPaymentMandate ? "Exporting..." : "Export Payment + Mandate"}
                    </button>
                </div>
            </div>

            {/* KPI */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1 w-full">
                    <Kpi label="Total Amount" value={kpi.totalAmount} />
                </div>
                <div className="flex-1 w-full">
                    <Kpi label="Completed Lot Amount" value={kpi.completedLotAmount} />
                </div>
                <div className="flex-1 w-full">
                    <Kpi label="WIP Amount" value={kpi.wipAmount} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* PAYROLL EXPORT BOX */}
                <div className="bg-white shadow-md rounded-xl p-6 border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                        Payroll Export Filter
                    </h2>
                    <div className="flex flex-col md:flex-row gap-4">
                        {/* From Date */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-600 mb-1">From Date</label>
                            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                        </div>
                        {/* To Date */}
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-600 mb-1">To Date</label>
                            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none" />
                        </div>
                        <div className="flex items-end">
                            <button onClick={exportPayroll} disabled={isExportingPayroll} className={`text-white px-5 py-2 rounded-lg text-sm font-medium shadow transition-all duration-300 ${isExportingPayroll ? "bg-purple-400 cursor-not-allowed opacity-75" : "bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg"}`}>
                                {isExportingPayroll ? "Exporting..." : "Export Payroll"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* UPLOAD ADVANCE RECOVERY BOX */}
                <div className="bg-white shadow-md rounded-xl p-6 border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">
                        Advance Recovery Upload
                    </h2>
                    <div className="flex flex-col">
                        <p className="text-sm text-gray-600 mb-4">
                            Upload an Excel file to deduct worker advances. A detailed recovery history will be recorded.
                        </p>
                        <input 
                            type="file" 
                            accept=".xlsx, .xls" 
                            className="hidden" 
                            ref={fileInputRef} 
                            onChange={handleFileChange} 
                        />
                        <div className="mt-2">
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isUploading}
                                className={`text-white px-5 py-2 rounded-lg text-sm font-medium shadow transition-all duration-300 ${
                                    isUploading
                                        ? "bg-blue-400 cursor-not-allowed opacity-75"
                                        : "bg-gradient-to-r from-blue-600 to-cyan-600 hover:shadow-lg"
                                }`}
                            >
                                {isUploading ? "Uploading..." : "Upload Advance Recovery"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* SUMMARY TABLE */}
            <Table 
                title="Worker Wages Monthly (Summary)"
                columns={summaryCols}
                rows={paginate(summaryRows, summaryPage)}
                page={summaryPage}
                setPage={setSummaryPage}
                total={summaryRows.length}
                loading={loading}
                rightAction={
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                        <div className="relative">
                            <input
                                ref={lastWagesDatePickerRef}
                                type="date"
                                value={lastWagesDate}
                                onChange={(e) => setLastWagesDate(e.target.value)}
                                aria-label="Select Last Wages Date"
                                tabIndex={-1}
                                className="absolute w-px h-px opacity-0 pointer-events-none"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const picker = lastWagesDatePickerRef.current;
                                    if (picker?.showPicker) picker.showPicker();
                                    else picker?.focus();
                                }}
                                className="border border-gray-300 rounded px-2 py-1 text-sm w-40 bg-white text-left flex items-center justify-between"
                                aria-label="Select Last Wages Date"
                            >
                                <span>{formatSelectedDate(lastWagesDate) || "DD-MM-YYYY"}</span>
                                <span aria-hidden="true">📅</span>
                            </button>
                        </div>
                        <button
                            onClick={saveLastWagesDate}
                            disabled={isSavingLastWagesDate}
                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm disabled:opacity-60"
                        >
                            {isSavingLastWagesDate ? "Saving..." : "Last Wages Date"}
                        </button>
                        <button
                            onClick={exportSummary}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                        >
                            Export Excel
                        </button>
                    </div>
                }
            />

            {/* LOT-WISE TABLE */}
            <Table 
                title="Worker Wages Monthly (Lot Wise)"
                columns={lotCols}
                rows={paginate(lotRows, lotPage)}
                page={lotPage}
                setPage={setLotPage}
                total={lotRows.length}
                loading={loading}
                rightAction={
                    <button onClick={exportLotWise} className="px-3 py-1 bg-green-600 text-white rounded text-sm">
                        Export Excel
                    </button>
                }
            />

            {/* PAYMENT LOGIC TABLE */}
            <Table 
                title="Wages Payment Logic"
                columns={paymentCols}
                rows={paginate(paymentLogicRows, paymentPage)}
                page={paymentPage}
                setPage={setPaymentPage}
                total={paymentLogicRows.length}
                loading={loading}
            />
        </div>
    );
}

/* =========================
   REUSABLE COMPONENTS
========================= */
const Kpi = ({ label, value }) => (
    <div className="bg-white border rounded shadow p-4">
        <div className="text-sm text-gray-500">{label}</div>
        <div className="text-xl font-semibold">
            ₹{Number(value || 0).toLocaleString("en-IN")}
        </div>
    </div>
);

const Table = ({ title, columns, rows, loading, page, setPage, total, rightAction }) => {
    useMisFilterAudit("wages-calculation", { table: title, page });
    const totalPages = Math.max(1, Math.ceil(total / 10));

    return (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
                <h2 className="font-semibold">{title}</h2>
                {rightAction}
            </div>

            {/* Table Container */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
                {loading ? (
                    <div className="p-8 text-center text-gray-500 animate-pulse">Loading…</div>
                ) : (
                    <table className="w-full text-sm text-left text-gray-600">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b border-gray-200">
                            <tr>
                                {columns.map((c) => (
                                    <th key={c} className="px-6 py-4 font-semibold whitespace-nowrap tracking-wider">{c}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="text-center p-8 text-gray-400">
                                        No data available
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                                        {columns.map((c) => (
                                            <td key={c} className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                {["date", "period", "created"].some(k => c.toLowerCase().includes(k))
                                                    ? formatDate(r[c])
                                                    : String(r[c] ?? "")}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="mt-2 flex justify-between text-sm">
                <span>Page {page} of {totalPages}</span>
                <div className="space-x-2">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
            </div>
        </div>
    );
};

export default WagesCalculator;
